import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { DialogService, SnackbarService, SnackbarStatus } from '@prism/angular-shared';
import { BehaviorSubject, EMPTY, catchError, finalize, first, from, interval, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { DataserverApiService } from '../shared/services/dataserver-api.service';
import { ScreenService } from '../shared/services/screen.service';
import { CoreActions, CoreSelectors } from '../shared/state/core';
import { ClientDevice, ClientDeviceInformationDialogArgs } from '@prism/prism-shared';
import { ClientDeviceInformationDialogComponent } from '../shared/components/client-device-information-dialog/client-device-information-dialog.component';
import { AppwriteAuthService } from '../shared/services/appwrite-auth.service';

const messages = [
  "scan your badge to login",
  "escanea tu insignia para iniciar sesión",
]

@Component({
  selector: 'prism-client-badge-login',
  templateUrl: './badge-login.component.html',
  styleUrls: ['./badge-login.component.scss'],
})
export class BadgeLoginComponent implements OnInit, OnDestroy {

  public loginInProgress$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public dataserverConnectionActive$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public currentMessage$: BehaviorSubject<string> = new BehaviorSubject<string>(messages[0]);
  public previousMessage$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  public switch$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public clientDevice$!: Observable<ClientDevice | null>;

  private accumulatedBadgeId: string = "";

  private unsubscribe$: Subject<void> = new Subject();

  constructor(
    private store: Store,
    private router: Router,
    private snackbarService: SnackbarService,
    private screenService: ScreenService,
    private dataserverApiService: DataserverApiService,
    private dialog: DialogService<void>,
    private auth: AppwriteAuthService,
  ) { }

  ngOnInit() {
    this.auth.signOut();
    this.store.dispatch(CoreActions.ClearCurrentUser());
    this.screenService.hideMainHeader$.next(true);

    this.clientDevice$ = this.store.select(CoreSelectors.getCurrentClientDevice);

    this.dataserverApiService.ping().pipe(
      first(),
      tap((response) => {
        this.dataserverConnectionActive$.next(response);
      })
    ).subscribe();

    interval(5000).pipe(
      tap(() => {
        this.dataserverApiService.ping().pipe(
          first(),
          tap((response) => {
            this.dataserverConnectionActive$.next(response);
          })
        ).subscribe();
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();


    interval(3000).pipe(
      tap((index) => {
        this.previousMessage$.next(this.currentMessage$.value);
        this.currentMessage$.next(messages[(index + 1) % messages.length])
        this.switch$.next(true);
        setTimeout(() => {
          this.previousMessage$.next(null);

        }, 500)
        setTimeout(() => {
          this.switch$.next(false);
        }, 2000)
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();
  }

  @HostListener('document:keypress', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.loginInProgress$.value) {
      return;
    }

    if (event.key === "Enter") {
      this.login();
    } else {
      this.accumulatedBadgeId += event.key;
    }
  }


  ngOnDestroy() {
    this.screenService.hideMainHeader$.next(false);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public login() {
    if (this.loginInProgress$.value) {
      return;
    }

    const badgeId = this.accumulatedBadgeId;
    this.accumulatedBadgeId = "";

    if (this.dataserverConnectionActive$.value) {
      if (badgeId) {
        this.loginInProgress$.next(true);

        this.dataserverApiService.getLoginTokenFromBadgeId(badgeId).pipe(
          first(),
          switchMap((response) => {
            const { userId, secret } = response;

            if (!userId || !secret) {
              this.snackbarService.showSnackbar(SnackbarStatus.ERROR, "badge not recognized");
              return EMPTY;
            }

            return from(this.auth.signInWithToken(userId, secret));
          }),
          tap((credential) => {
            if (!credential?.user) {
              this.snackbarService.showSnackbar(SnackbarStatus.ERROR, "error logging in");
              return;
            }

            this.router.navigate(['/']);
            this.snackbarService.showSnackbar(SnackbarStatus.SUCCESS, "welcome!");
            this.currentMessage$.next("success!");
            this.previousMessage$.next(null);
            this.store.dispatch(CoreActions.LoadCurrentUser({ userId: credential.user.uid }));
          }),
          catchError((error) => {
            this.snackbarService.showSnackbar(SnackbarStatus.ERROR, "error logging in");
            console.error(error);
            return EMPTY;
          }),
          finalize(() => {
            this.loginInProgress$.next(false);
          }),
          takeUntil(this.unsubscribe$),
        ).subscribe();
      }
    } else {
      this.snackbarService.showSnackbar(SnackbarStatus.ERROR, "no connection to dataserver");
    }

  }

  public launchClientDeviceInformationDialog(clientDevice: ClientDevice) {

    const args: ClientDeviceInformationDialogArgs = {
      clientDevice
    }

    const title = clientDevice.name;

    this.dialog.openDialog(ClientDeviceInformationDialogComponent, title, { args });

  }

  public navigateToEmailLoginPage() {
    this.router.navigate(['/login']);
  }
}
